/**
 * Enhanced Category Section Component
 * 
 * Features:
 * - Glowing themed icons with pulse animation
 * - Rich descriptions and taglines
 * - Dynamic market snapshot
 * - "What to Watch" callouts
 * - Conviction meter
 * - Section status indicator
 * - Gradient dividers
 */

import { el } from '../../shared/components/simpleRender.js';
import { 
  SECTION_INTELLIGENCE, 
  calculateSectionConviction, 
  getSectionStatus 
} from '../data/sectionIntelligence.js';

// Re-export for backwards compatibility
export const CATEGORIES = Object.fromEntries(
  Object.entries(SECTION_INTELLIGENCE).map(([key, val]) => [
    key, 
    { name: val.name, description: val.description, icon: val.icon, color: val.color }
  ])
);

const STATUS_CONFIG = {
  low: { label: 'CALM', color: 'var(--tg-fog-green)', icon: '🟢' },
  normal: { label: 'NORMAL', color: 'var(--tg-fog-green)', icon: '🟢' },
  neutral: { label: 'NORMAL', color: 'var(--tg-fog-green)', icon: '🟢' },
  elevated: { label: 'ELEVATED', color: '#f97316', icon: '🟡' },
  high: { label: 'HIGH', color: 'var(--tg-rust-red)', icon: '🟠' },
  extreme: { label: 'EXTREME', color: 'var(--tg-flash-crash)', icon: '🔴' },
};

/**
 * Build the section header with glowing icon
 */
function buildSectionHeader(intel, signalCount, status, isCollapsed, onToggle) {
  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.normal;
  
  return el('div', { className: 'section-header' }, [
    // Left side: Icon + Title + Tagline
    el('div', { className: 'section-header__left' }, [
      el('div', { 
        className: 'section-header__icon-wrapper',
        style: `--section-glow: ${intel.glowColor}`,
      }, [
        el('span', { className: 'section-header__icon' }, [intel.icon]),
      ]),
      el('div', { className: 'section-header__titles' }, [
        el('div', { className: 'section-header__title-row' }, [
          el('h2', { 
            className: 'section-header__title',
            style: `color: ${intel.color}`,
          }, [intel.name.toUpperCase()]),
          el('span', { className: 'section-header__count' }, [`${signalCount} signals`]),
          el('span', { 
            className: 'section-header__status',
            style: `color: ${statusCfg.color}; border-color: ${statusCfg.color}`,
          }, [statusCfg.label]),
        ]),
        el('p', { className: 'section-header__tagline' }, [`— ${intel.tagline}`]),
      ]),
    ]),
    // Right side: Collapse toggle
    el('button', { 
      className: 'section-header__toggle',
      onclick: onToggle,
      'aria-label': isCollapsed ? 'Expand section' : 'Collapse section',
    }, [
      el('span', { className: 'section-header__toggle-icon' }, [isCollapsed ? '▼' : '▲']),
    ]),
  ]);
}

/**
 * Build the description panel
 */
function buildDescriptionPanel(intel) {
  return el('div', { className: 'section-description-panel' }, [
    el('p', { className: 'section-description-panel__text' }, [intel.description]),
  ]);
}

/**
 * Build the market snapshot box
 */
function buildSnapshotBox(intel, signals, status) {
  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.normal;
  const snapshotText = intel.generateSnapshot ? intel.generateSnapshot(signals) : 'Analyzing signals...';
  
  return el('div', { 
    className: 'section-snapshot',
    style: `border-left-color: ${intel.color}`,
  }, [
    el('div', { className: 'section-snapshot__header' }, [
      el('span', { className: 'section-snapshot__icon' }, [intel.icon]),
      el('span', { className: 'section-snapshot__label' }, [`${intel.name.toUpperCase()} SNAPSHOT`]),
    ]),
    el('p', { className: 'section-snapshot__text' }, [snapshotText]),
  ]);
}

/**
 * Build the "What to Watch" callouts
 */
function buildWatchList(intel) {
  if (!intel.watchItems || intel.watchItems.length === 0) return null;
  
  const priorityConfig = {
    high: { icon: '🔴', color: 'var(--tg-rust-red)' },
    medium: { icon: '🟡', color: '#f97316' },
    low: { icon: '🟢', color: 'var(--tg-fog-green)' },
  };
  
  return el('div', { className: 'section-watch' }, [
    el('div', { className: 'section-watch__header' }, [
      el('span', { className: 'section-watch__icon' }, ['👁️']),
      el('span', { className: 'section-watch__label' }, ['WHAT TO WATCH']),
    ]),
    el('ul', { className: 'section-watch__list' },
      intel.watchItems.map(item => {
        const cfg = priorityConfig[item.priority] || priorityConfig.medium;
        return el('li', { 
          className: 'section-watch__item',
          style: `--watch-color: ${cfg.color}`,
        }, [
          el('span', { className: 'section-watch__bullet' }, [cfg.icon]),
          el('span', { className: 'section-watch__text' }, [item.text]),
        ]);
      })
    ),
  ]);
}

/**
 * Build the conviction meter
 */
function buildConvictionMeter(signals, sectionColor) {
  const conviction = calculateSectionConviction(signals);
  
  const barSegments = [];
  for (let i = 0; i < 10; i++) {
    const filled = i < Math.floor(conviction.percent / 10);
    barSegments.push(
      el('div', { 
        className: `conviction-bar__segment ${filled ? 'conviction-bar__segment--filled' : ''}`,
        style: filled ? `background: ${sectionColor}` : '',
      }, [])
    );
  }
  
  return el('div', { className: 'section-conviction' }, [
    el('div', { className: 'section-conviction__header' }, [
      el('span', { className: 'section-conviction__label' }, ['SIGNAL ALIGNMENT']),
      el('span', { className: 'section-conviction__value' }, [`${conviction.percent}% ${conviction.label}`]),
    ]),
    el('div', { className: 'conviction-bar' }, barSegments),
    el('div', { className: 'section-conviction__detail' }, [
      `${conviction.aligned} of ${conviction.total} signals pointing ${conviction.dominantLevel?.toUpperCase() || 'N/A'}`,
    ]),
  ]);
}

/**
 * Build historical context box
 */
function buildHistoricalContext(intel) {
  if (!intel.historicalContext) return null;
  
  const ctx = intel.historicalContext;
  
  return el('div', { className: 'section-history' }, [
    el('div', { className: 'section-history__header' }, [
      el('span', { className: 'section-history__icon' }, ['📅']),
      el('span', { className: 'section-history__label' }, ['HISTORICAL CONTEXT']),
    ]),
    el('div', { className: 'section-history__markers' }, [
      el('div', { className: 'section-history__marker' }, [
        el('span', { className: 'section-history__marker-label' }, ['Peak']),
        el('span', { className: 'section-history__marker-value section-history__marker-value--high' }, [ctx.peakValue]),
        el('span', { className: 'section-history__marker-date' }, [ctx.peakDate]),
      ]),
      el('div', { className: 'section-history__marker' }, [
        el('span', { className: 'section-history__marker-label' }, ['Trough']),
        el('span', { className: 'section-history__marker-value section-history__marker-value--low' }, [ctx.troughValue]),
        el('span', { className: 'section-history__marker-date' }, [ctx.troughDate]),
      ]),
    ]),
    el('p', { className: 'section-history__narrative' }, [ctx.narrative]),
  ]);
}

/**
 * Build section footer with last update
 */
function buildSectionFooter(signals) {
  // Find most recent update
  let latestDate = null;
  signals.forEach(s => {
    if (s.current?.date) {
      const d = new Date(s.current.date);
      if (!latestDate || d > latestDate) latestDate = d;
    }
  });
  
  const dateStr = latestDate 
    ? latestDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'N/A';
  
  return el('div', { className: 'section-footer' }, [
    el('span', { className: 'section-footer__update' }, [`Last updated: ${dateStr}`]),
  ]);
}

/**
 * Main category section creator
 */
export function createCategorySection(categoryKey, signalCards = [], isCollapsed = false, signals = []) {
  const intel = SECTION_INTELLIGENCE[categoryKey] || {
    name: categoryKey,
    icon: '📊',
    color: 'var(--tg-fog-green)',
    tagline: 'Market signals',
    description: 'Signal data for this category.',
    glowColor: 'rgba(151, 184, 160, 0.4)',
    accentGradient: 'linear-gradient(135deg, rgba(151, 184, 160, 0.15) 0%, transparent 100%)',
    watchItems: [],
    generateSnapshot: () => 'Analyzing...',
  };
  
  let collapsed = isCollapsed;
  const status = getSectionStatus(signals);
  
  // Build all content elements
  const contentWrapper = el('div', { className: 'section-content' }, []);
  const grid = el('div', { className: 'category-section__grid' }, signalCards);
  
  // Build intelligence panels (will be populated after signals load)
  const intelPanels = el('div', { className: 'section-intel-panels' }, [
    buildSnapshotBox(intel, signals, status),
    el('div', { className: 'section-intel-row' }, [
      buildWatchList(intel),
      signals.length > 1 ? buildConvictionMeter(signals, intel.color) : null,
    ].filter(Boolean)),
    buildHistoricalContext(intel),
  ]);
  
  contentWrapper.appendChild(buildDescriptionPanel(intel));
  contentWrapper.appendChild(intelPanels);
  contentWrapper.appendChild(grid);
  contentWrapper.appendChild(buildSectionFooter(signals));
  
  if (collapsed) contentWrapper.style.display = 'none';
  
  // Toggle function
  const toggleSection = () => {
    collapsed = !collapsed;
    contentWrapper.style.display = collapsed ? 'none' : '';
    const toggleIcon = section.querySelector('.section-header__toggle-icon');
    if (toggleIcon) toggleIcon.textContent = collapsed ? '▼' : '▲';
  };
  
  const section = el('div', { 
    className: `category-section category-section--${status}`,
    'data-category': categoryKey,
    style: `--section-accent: ${intel.color}; --section-gradient: ${intel.accentGradient}`,
  }, [
    // Gradient accent bar
    el('div', { className: 'section-accent-bar' }, []),
    buildSectionHeader(intel, signalCards.length, status, collapsed, toggleSection),
    el('div', { className: 'section-divider' }, []),
    contentWrapper,
  ]);
  
  return section;
}

/**
 * Update section with loaded signals
 */
export function updateCategorySection(sectionEl, signals) {
  if (!sectionEl || !signals) return;
  
  const categoryKey = sectionEl.dataset.category;
  const intel = SECTION_INTELLIGENCE[categoryKey];
  if (!intel) return;
  
  const status = getSectionStatus(signals);
  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.normal;
  
  // Update status badge
  const statusBadge = sectionEl.querySelector('.section-header__status');
  if (statusBadge) {
    statusBadge.textContent = statusCfg.label;
    statusBadge.style.color = statusCfg.color;
    statusBadge.style.borderColor = statusCfg.color;
  }
  
  // Update section class
  sectionEl.className = sectionEl.className.replace(/category-section--\w+/g, '');
  sectionEl.classList.add(`category-section--${status}`);
  
  // Update snapshot
  const snapshotText = sectionEl.querySelector('.section-snapshot__text');
  if (snapshotText && intel.generateSnapshot) {
    snapshotText.textContent = intel.generateSnapshot(signals);
  }
  
  // Update conviction meter
  const convictionSection = sectionEl.querySelector('.section-conviction');
  if (convictionSection) {
    const newConviction = buildConvictionMeter(signals, intel.color);
    convictionSection.replaceWith(newConviction);
  }
  
  // Update footer date
  let latestDate = null;
  signals.forEach(s => {
    if (s.current?.date) {
      const d = new Date(s.current.date);
      if (!latestDate || d > latestDate) latestDate = d;
    }
  });
  const footerUpdate = sectionEl.querySelector('.section-footer__update');
  if (footerUpdate && latestDate) {
    footerUpdate.textContent = `Last updated: ${latestDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }
}

/**
 * Placeholder section for loading state
 */
export function createCategorySectionPlaceholder(categoryKey, count = 2) {
  const intel = SECTION_INTELLIGENCE[categoryKey] || {
    name: categoryKey,
    icon: '📊',
    color: 'var(--tg-fog-green)',
    tagline: 'Loading...',
    glowColor: 'rgba(151, 184, 160, 0.4)',
  };
  
  const placeholders = Array(count).fill(null).map(() => 
    el('div', { className: 'signal-card signal-card--loading' }, [
      el('div', { className: 'signal-card__skeleton signal-card__skeleton--title' }, []),
      el('div', { className: 'signal-card__skeleton signal-card__skeleton--value' }, []),
      el('div', { className: 'signal-card__skeleton signal-card__skeleton--chart' }, []),
    ])
  );

  return el('div', { 
    className: 'category-section category-section--loading',
    'data-category': categoryKey,
    style: `--section-accent: ${intel.color}`,
  }, [
    el('div', { className: 'section-accent-bar' }, []),
    el('div', { className: 'section-header' }, [
      el('div', { className: 'section-header__left' }, [
        el('div', { 
          className: 'section-header__icon-wrapper',
          style: `--section-glow: ${intel.glowColor}`,
        }, [
          el('span', { className: 'section-header__icon' }, [intel.icon]),
        ]),
        el('div', { className: 'section-header__titles' }, [
          el('div', { className: 'section-header__title-row' }, [
            el('h2', { 
              className: 'section-header__title',
              style: `color: ${intel.color}`,
            }, [intel.name.toUpperCase()]),
          ]),
          el('p', { className: 'section-header__tagline section-header__tagline--loading' }, ['Loading signals...']),
        ]),
      ]),
    ]),
    el('div', { className: 'section-divider' }, []),
    el('div', { className: 'section-content' }, [
      el('div', { className: 'section-snapshot section-snapshot--loading' }, [
        el('div', { className: 'signal-card__skeleton' }, []),
      ]),
      el('div', { className: 'category-section__grid' }, placeholders),
    ]),
  ]);
}

export { SECTION_INTELLIGENCE };
